import React from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ExternalLink } from "lucide-react";
import BoringAvatar from "boring-avatars";

interface FeedbackItem {
  id: number;
  created_at: string;
  updated_at?: string;
  help_center?: { title: string; slug?: string };
  user_hash?: string;
  feedback_type: string;
  item?: { title: string; slug?: string };
  item_id: number;
  rating: number | null;
  issue_types?: string[];
  feedback_text?: string;
  browser_info?: {
    browser?: string;
    os?: string;
    screen?: string;
    device?: string;
  };
}

interface FeedbackDetailsDrawerProps {
  feedback: FeedbackItem | null;
  isOpen: boolean;
  onClose: () => void;
  formatDate: (dateString: string) => string;
  getRatingIcon: (rating: number | null) => React.ReactNode;
  getItemUrl: (item: FeedbackItem) => string | null;
  onOpenItem: (url: string) => void;
}

export const FeedbackDetailsDrawer: React.FC<FeedbackDetailsDrawerProps> = ({
  feedback,
  isOpen,
  onClose,
  formatDate,
  getRatingIcon,
  getItemUrl,
  onOpenItem,
}) => {
  const { t } = useTranslation();
  if (!feedback) return null;
 /*
         <div className="font-medium">
                
                </div>
                */
  const itemUrl = getItemUrl(feedback);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>{t("reports.drawer.title")}</SheetTitle>
          <SheetDescription>
            {t("reports.drawer.leftAt", { date: formatDate(feedback.created_at) })}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="grid grid-cols-2 gap-2">
            {/* User Information */}
            {feedback.user_hash && (
              <div>
                <h3 className="text-sm font-semibold mb-2">{t("reports.drawer.user")}</h3>
                <div className="flex items-center gap-3">
                  <BoringAvatar
                    name={feedback.user_hash}
                    variant="beam"
                    size={48}
                  />
                  <div>
                    <div className="text-sm font-medium">{t("reports.drawer.anonymousUser")}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {feedback.user_hash}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Item Information */}
            <div>
              <h3 className="text-sm font-semibold mb-2">{t("reports.drawer.item")}</h3>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{feedback.feedback_type}</Badge>
                  <span className="text-sm font-medium">
                    {feedback.item?.title || t("reports.table.itemIdFallback", { id: feedback.item_id })}
                  </span>
                  {itemUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2"
                      onClick={() => onOpenItem(itemUrl)}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      {t("common.open")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Rating */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <h3 className="text-sm font-semibold mb-2">{t("reports.drawer.rating")}</h3>
              <div className="flex items-center gap-2">
                {getRatingIcon(feedback.rating)}
                <span className="text-sm">
                  {feedback.rating === 1
                    ? t("reports.drawer.ratingPositive")
                    : feedback.rating === 0
                    ? t("reports.drawer.ratingNegative")
                    : t("reports.noRating")}
                </span>
              </div>
            </div>

            {/* Issue Types */}
            {feedback.issue_types && feedback.issue_types.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">{t("reports.drawer.issueTypes")}</h3>
                <div className="space-y-2">
                  {feedback.issue_types.map((issue: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="text-xs bg-gray-100 whitespace-pre-wrap bg-muted/50 p-3 rounded-md">
                        {issue}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Feedback Text */}
          {feedback.feedback_text && (
            <>
              <div>
                <h3 className="text-sm font-semibold mb-2">{t("reports.drawer.feedbackText")}</h3>
                <p className="text-sm text-foreground bg-gray-100 whitespace-pre-wrap bg-muted/50 p-3 rounded-md">
                  {feedback.feedback_text}
                </p>
              </div>
              <Separator />
            </>
          )}

          {/* Browser Information */}
          {feedback.browser_info && (
            <div>
              <h3 className="text-sm font-semibold mb-2">
                {t("reports.drawer.browserInfo")}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-muted-foreground">{t("reports.drawer.browser")}</span>
                    <div className="font-medium">
                      {feedback.browser_info.browser || t("common.unknown")}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t("reports.drawer.os")}</span>
                    <div className="font-medium">
                      {feedback.browser_info.os || t("common.unknown")}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t("reports.drawer.screen")}</span>
                    <div className="font-medium">
                      {feedback.browser_info.screen || t("common.unknown")}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t("reports.drawer.device")}</span>
                    <div className="font-medium">
                      {feedback.browser_info.device || t("common.unknown")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* Metadata */}
        </div>
      </SheetContent>
    </Sheet>
  );
};
